import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import {
	Button,
	Col,
	Form,
	FormFeedback,
	FormGroup,
	Input,
	Label,
	Row,
	TabContent,
	TabPane,
	Nav,
	NavItem,
	NavLink,
} from "reactstrap";
import withRouter from "../../../components/Common/withRouter";
import { useNavigate, useLocation } from "react-router-dom";
import actionPlansService from "services/ActionPlansService";
import objectiveActionPlansService from "services/ObjectiveActionPlansService";
import OrganizationalStructureService from "services/OrganizationalStructureService";
import Select from "react-select";
import { registerLocale } from "react-datepicker";
import pt from "date-fns/locale/pt-BR";
import Swal from "sweetalert2";
import userService from "services/UserService";
import Objetivos from "./objetivos";
import ModalCreateEditObjective from "./ModalCreateEditObjective";
import Loading from "components/Common/Loading";
import { Divider } from "@mui/material";
import { ArrowLeft, Copy, FloppyDisk, Plus, Printer } from "@phosphor-icons/react";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";
import ObjetivosAccordion from "./ObjetivosAccordion";
import permissionsService from "services/permissionsService";
import CopyActionPlanModal from "./CopyActionPlanModal";
import actionObjectiveActionPlansService from "services/ActionObjectivesActionPlansService";
import { generateYearOptions } from "helpers/yearHelper";

registerLocale("pt-BR", pt);

const PlanosDeAcaoForm = (props) => {
	const [formData, setFormData] = useState({
		guideline: "",
		deadline: null,
		approving_id: null,
		responsible_id: null,
		organizational_structure_id: null,
		collaborators: [],
		year_of_use: null,
	});
	const [formErrors, setFormErrors] = useState({});
	const [data, setData] = useState(null);
	const [usuarios, setUsuarios] = useState([]);
	const [selectedOptions, setSelectedOptions] = useState([]);
	const [organizacoes, setOrganizacoes] = useState([]);
	const [objetivos, setObjetivos] = useState([]);
	const [openModalObj, setOpenModalObj] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingButton, setIsLoadingButton] = useState(false);
	const [activeTab, setActiveTab] = useState("1");
	const [temPermissaoCopiar, setTemPermissaoCopiar] = useState(false);
	const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
	const [loggedUserId, setLoggedUserId] = useState(null);

	document.title = "Senda | Plano de Ação";

	const params = props.router.params;
	const id = params?.id;
	const location = useLocation();
	const isInactivesRoute = location.pathname.includes("/inactives");

	const navigate = useNavigate();

	const years = generateYearOptions();

	const fetchUsuarios = async () => {
		try {
			const response = await userService.get();
			const funcoes = response.map((item) => ({
				value: item.id,
				label: item.lastname,
			}));
			setUsuarios(funcoes);
		} catch (error) {
			console.log(error);
		}
	};

	const fetchOrganizacoes = async () => {
		try {
			const response = await OrganizationalStructureService.get();

			const options = response.map((item) => ({
				value: item.id,
				label: item.name,
			}));

			setOrganizacoes(options);
		} catch (error) {
			console.log(error);
		}
	};

	const fetchObjetivos = useCallback(async () => {
		if (!id) return;
		
		try {
			let objetivos;
			if (isInactivesRoute) {
				objetivos = await objectiveActionPlansService.getInactives({
					action_plans_id: id,
				});
			} else {
				objetivos = await objectiveActionPlansService.get({
					action_plans_id: id,
				});
			}
			setObjetivos(objetivos);
		} catch (error) {
			console.log(error);
		}
	}, [id, isInactivesRoute]);

	// Função para verificar se um objetivo precisa ser atualizado
	const shouldUpdateObjective = (objetivo) => {
		if (!objetivo.actions || objetivo.actions.length === 0) {
			return false; // Objetivo sem actions não é verificado
		}

		// Não atualiza objetivos em STANDBY ou CANCELADO (deixar como estão)
		if (objetivo.status === "STANDBY" || objetivo.status === "CANCELADO" ||
			objetivo.status === "standby" || objetivo.status === "cancelado") {
			return false;
		}

		// Só verifica actions com status "OK" ou "ANDAMENTO" (ignora STANDBY, CANCELADO, etc.)
		const relevantActions = objetivo.actions.filter(action =>
			action.status === "OK" || action.status === "ok" ||
			action.status === "ANDAMENTO" || action.status === "andamento"
		);

		// Se não há actions relevantes, não atualiza
		if (relevantActions.length === 0) {
			return false;
		}

		const allRelevantActionsOk = relevantActions.every(action =>
			action.status === "OK" || action.status === "ok"
		);

		// Se todas as actions relevantes estão OK mas o objetivo não está OK
		if (allRelevantActionsOk && objetivo.status !== "OK" && objetivo.status !== "ok") {
			return { shouldUpdate: true, newStatus: "OK" };
		}

		// Se nem todas as actions relevantes estão OK mas o objetivo está OK
		if (!allRelevantActionsOk && (objetivo.status === "OK" || objetivo.status === "ok")) {
			return { shouldUpdate: true, newStatus: "ANDAMENTO" };
		}

		return false; // Não precisa atualizar
	};


	// Função para verificar todos os objetivos
	const checkAllObjectivesStatus = async () => {
		try {
			const objetivosToUpdate = [];

			// Verifica quais objetivos precisam ser atualizados
			for (const objetivo of objetivos) {
				const updateInfo = shouldUpdateObjective(objetivo);
				if (updateInfo) {
					objetivosToUpdate.push({
						...objetivo,
						newStatus: updateInfo.newStatus
					});
				}
			}

			if (objetivosToUpdate.length === 0) {
				console.log("Nenhum objetivo precisa ser atualizado");
				return;
			}

			// Atualiza todos os objetivos que precisam ser modificados
			for (const objetivo of objetivosToUpdate) {
				await objectiveActionPlansService.put(objetivo.id, {
					...objetivo,
					status: objetivo.newStatus
				});
				console.log(`Objetivo ${objetivo.id} atualizado para status "${objetivo.newStatus}"`);
			}

			await fetchObjetivos();
			console.log(`${objetivosToUpdate.length} objetivos atualizados`);
		} catch (error) {
			console.error("Erro ao verificar status dos objetivos:", error);
		}
	};

	const fetchData = async () => {
		try {
			let response;
			if (isInactivesRoute) {
				response = await actionPlansService.getInactiveById(id);
			} else {
				response = await actionPlansService.getById(id);
			}
			const collaboratorsIds = response.collaborators.map(
				(item) => item.id,
			);
			setFormData({
				guideline: response.guideline || "",
				deadline: response.deadline || "",
				approving_id: response.approving_id || null,
				responsible_id: response.responsible_id || null,
				organizational_structure_id:
					response.organizational_structure_id || null,
				collaborators: collaboratorsIds || [],
				year_of_use: response.year_of_use || null,
			});
			setData(response);
		} catch (error) {
			console.log(error);
		}
	};

	const getPermissions = async () => {
		try {
			// Pegar o ID do usuário logado
			const userPermission = await permissionsService.getUserPermission();
			setLoggedUserId(userPermission.user_id);

			const response = await permissionsService.getPermissions();
			for (let permission of response) {
				if (permission.role === "actionPlanCopy") {
					setTemPermissaoCopiar(true);
				}
			}
		} catch (error) {
			console.log(error);
		}
	};

	const getAll = async () => {
		setIsLoading(true);
		if (id) {
			await fetchData();
			// fetchObjetivos será chamado após fetchData atualizar o estado data
		}
		await fetchUsuarios();
		await fetchOrganizacoes();
		await getPermissions();
		setIsLoading(false);
	};

	useEffect(() => {
		getAll();
	}, [id]);

	// Buscar objetivos quando os dados do plano estiverem disponíveis
	useEffect(() => {
		if (id && data) {
			fetchObjetivos();
		}
	}, [id, data, fetchObjetivos]);

	useEffect(() => {
		setSelectedOptions(
			usuarios.filter((option) =>
				formData.collaborators.includes(option.value),
			),
		);
	}, [usuarios, formData.collaborators]);

	// useEffect para verificar automaticamente o status dos objetivos
	useEffect(() => {
		if (objetivos.length > 0) {
			checkAllObjectivesStatus();
		}
	}, [objetivos]);

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	const handleSelectChange = (selectedOption, fieldName) => {
		const selectedValue = selectedOption ? selectedOption.value : null;
		setFormData({ ...formData, [fieldName]: selectedValue });
	};

	const handleMultiSelectChange = (selectedOption) => {
		setSelectedOptions(selectedOption);
		const selectedValues = selectedOption
			? selectedOption.map((option) => option.value)
			: [];
		setFormData({ ...formData, collaborators: selectedValues });
	};

	const validate = () => {
		const errors = {};
		if (!formData.guideline) {
			errors.guideline =
				"Por favor, informe a diretriz do plano de ação!";
		} else if (formData.guideline.length > 150) {
			errors.guideline =
				"A diretriz do plano de ação deve ter no máximo 150 caracteres!";
		}
		if (!formData.deadline) {
			errors.deadline =
				"Por favor, informe o prazo final do plano de ação!";
		}
		if (!formData.organizational_structure_id) {
			errors.organizational_structure_id = "Campo obrigatório.";
		}
		if (!formData.approving_id) {
			errors.approving_id = "Campo obrigatório.";
		}
		if (!formData.responsible_id) {
			errors.responsible_id = "Campo obrigatório.";
		}
		// Adicione outras validações conforme necessário
		return errors;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const errors = validate();
		if (Object.keys(errors).length > 0) {
			setFormErrors(errors);

			// Montar a mensagem de erro com os nomes dos campos
			let planoMessage = "";
			let configuracaoMessage = "";

			// Verifica os campos obrigatórios da aba "Plano"
			if (errors.guideline || errors.deadline) {
				planoMessage = `
				<strong>Aba Plano:</strong><br/>
				${errors.guideline ? "<p>O campo Diretriz é obrigatório.</p>" : ""}
				${errors.deadline ? "<p>O campo Prazo Final é obrigatório.</p>" : ""}
			  `;
			}

			// Verifica os campos obrigatórios da aba "Configuração"
			if (
				errors.organizational_structure_id ||
				errors.approving_id ||
				errors.responsible_id
			) {
				configuracaoMessage = `
				<strong>Aba Configuração:</strong><br/>
				${errors.organizational_structure_id
						? "<p>O campo Departamento é obrigatório.</p>"
						: ""
					}
				${errors.approving_id ? "<p>O campo Aprovador é obrigatório.</p>" : ""}
				${errors.responsible_id ? "<p>O campo Responsável é obrigatório.</p>" : ""}
			  `;
			}

			// Exibe o Swal com a mensagem personalizada
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Campos obrigatórios não preenchidos",
				html: `
				<p>Por favor, preencha os campos obrigatórios:</p>
				${planoMessage}
				${configuracaoMessage}
			  `,
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		} else {
			setIsLoadingButton(true);
			try {
				if (id) {
					// Edição: mantém o usuário na página
					await actionPlansService.put(id, formData);
					SendaCustomSwal.fire({
						iconHtml: '<i class="success-icon"></i>',
						title: "Sucesso",
						text: "Plano de ação editado com sucesso!",
						customClass: {
							confirmButton: "custom-confirm-button",
						},
					}).then(() => {
						setActiveTab("1");
					});
				} else {
					// Criação: redireciona para o plano criado
					const response = await actionPlansService.post(formData);
					const newActionPlanId = response.id;

					SendaCustomSwal.fire({
						iconHtml: '<i class="success-icon"></i>',
						title: "Sucesso",
						text: "Plano de ação criado com sucesso!",
						customClass: {
							confirmButton: "custom-confirm-button",
						},
					}).then(() => {
						if (newActionPlanId) {
							// Usa replace: true para substituir a entrada de criação no histórico
							// Assim, ao voltar, vai para a lista de planos ao invés da página de criação
							navigate(`/planos-de-acao/form/${newActionPlanId}`, { replace: true });
							setActiveTab("1");
						} else {
							navigate("/planos-de-acao");
						}
					});
				}
			} catch (error) {
				SendaCustomSwal.fire({
					iconHtml: '<i class="error-icon"></i>',
					title: "Oops...",
					text: "Algo deu errado!",
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				});
			} finally {
				setIsLoadingButton(false);
			}
		}
	};

	const setObjectiveModal = () => {
		setOpenModalObj(true);
	};

	const onCloseModalObj = () => {
		setOpenModalObj(false);
	};

	const handleCopyActionPlan = () => {
		setIsCopyModalOpen(true);
	};

	const handleConfirmCopy = async (copyData) => {
		try {
			setIsLoading(true);

			// Dados para criar o novo plano de ação
			const newActionPlanData = {
				guideline: data?.guideline,
				deadline: data?.deadline,
				approving_id: loggedUserId, // Usar ID do usuário logado
				responsible_id: loggedUserId, // Usar ID do usuário logado
				organizational_structure_id: copyData.department_id,
				year_of_use: data?.year_of_use,
			};

			console.log("Criando novo plano de ação:", newActionPlanData);
			console.log("Empresa ID:", copyData.company_id);
			console.log("Departamento ID:", copyData.department_id);

			// Criar o novo plano de ação
			const newActionPlanResponse = await actionPlansService.postCopyActionPlan(newActionPlanData, copyData.company_id);
			const newActionPlan = newActionPlanResponse.data;
			console.log("Plano de ação criado com sucesso:", newActionPlan);

			// Criar os objetivos do plano de ação
			if (objetivos && objetivos.length > 0) {
				for (const objective of objetivos) {
					// Dados do objetivo
					const objectiveData = {
						objective: objective.objective,
						deadline: objective.deadline,
						responsible_id: loggedUserId,
						action_plans_id: newActionPlan.id, // Usar o ID do novo plano criado
						generates_cost: objective.generates_cost,
						status: objective.status,
					};

					console.log("Criando objetivo:", objectiveData);

					// Criar o objetivo
					const objectiveResponse = await objectiveActionPlansService.postCopyObjective(objectiveData, copyData.company_id);
					const newObjective = objectiveResponse.data;
					console.log("Objetivo criado com sucesso:", newObjective);

					// Criar as ações do objetivo
					if (objective.actions && objective.actions.length > 0) {
						for (const action of objective.actions) {
							const actionData = {
								objectives_action_plans_id: newObjective.id, // ID do novo objetivo criado
								responsible_id: loggedUserId,
								action: action.action,
								cost: action.cost,
								deadline: action.deadline,
								status: action.status,
								obs: action.obs,
							};

							console.log("Criando ação:", actionData);

							const actionResponse = await actionObjectiveActionPlansService.postCopyActionObjective(actionData, copyData.company_id);
							console.log("Ação criada com sucesso:", actionResponse);
						}
					}
				}
			}

			SendaCustomSwal.fire({
				iconHtml: '<i class="success-icon"></i>',
				title: "Sucesso",
				text: "Plano de ação copiado com sucesso!",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});

		} catch (error) {
			console.error("Erro ao copiar plano de ação:", error);
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Erro",
				text: "Erro ao copiar plano de ação!",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		} finally {
			setIsLoading(false);
		}
	};


	const customStyles = {
		option: (provided, state) => ({
			...provided,
			color: state.isSelected ? "blue" : "black",
			backgroundColor: "white",
			"&:hover": {
				backgroundColor: "#556ee6",
			},
		}),
	};

	return (
		<React.Fragment>
			<Loading isLoading={isLoading}>
				<div className="page-content">
					{/* <Breadcrumbs
					title="Configurações"
					breadcrumbItem="Cadastro de Plano de Ação"
					backLink="/planos-de-acao"
				/> */}

					<div
						className="container-fluid"
						style={{
							backgroundColor: "white",
							padding: 20,
							borderRadius: 10,
							marginTop: 25,
						}}
					>
						<Form onSubmit={handleSubmit}>
							<Row>
								<Col md="6">
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: "8px",
										}}
									>
										<div
											style={{
												display: "flex",
												alignItems: "center",
												gap: "8px",
												cursor: "pointer",
											}}
											onClick={() => window.history.back()}
										>
											<ArrowLeft
												size={20}
												style={{
													color: "black",
												}}
											/>
											<h4
												style={{
													fontSize: "18px",
													fontWeight: "bold",
													margin: 0,
												}}
											>
												Planos de Ação
											</h4>
										</div>
									</div>
								</Col>

								<Col md="6" className="text-end">
									<div
										style={{
											display: "flex",
											justifyContent: "flex-end",
											gap: "10px",
										}}
									>
										{data?.id && temPermissaoCopiar && (
											<Button
												onClick={() => handleCopyActionPlan()}
												style={{
													background: "none",
													padding: 10,
													cursor: "pointer",
													color: "black",
												}}
											>
												<Copy size={16} /> Copiar
											</Button>
										)}
										{data?.id && (
											<Button
												onClick={() => {
													const baseUrl =
														window.location.origin +
														window.location.pathname;
													const url = `${baseUrl}#/print/pdf/plano-acao?id=${id}`;
													window.open(url, "_blank");
												}}
												style={{
													background: "none",
													padding: 10,
													cursor: "pointer",
													color: "black",
												}}
											>
												<Printer size={16} /> Imprimir
											</Button>
										)}

										<Button
											type="submit"
											style={{
												background: "none",
												padding: 10,
												cursor: "pointer",
												color: "black",
												width: 150,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												gap: 2,
											}}
											disabled={isLoadingButton}
										>
											{isLoadingButton ? (
												<span
													className="spinner-border spinner-border-sm mr-1"
													style={{ marginRight: 10 }}
												></span>
											) : null}
											<FloppyDisk size={16} /> Salvar
										</Button>
									</div>
								</Col>
							</Row>
							{/* Navegação entre as abas */}
							<Nav tabs>
								<NavItem>
									<NavLink
										className={
											activeTab === "1" ? "active" : ""
										}
										onClick={() => setActiveTab("1")}
										style={{
											cursor: "pointer",
											color:
												activeTab === "1"
													? "#2a3042"
													: "inherit",
										}}
										onMouseEnter={(e) =>
											(e.target.style.color = "#2a3042")
										}
										onMouseLeave={(e) =>
										(e.target.style.color =
											activeTab === "1"
												? "#2a3042"
												: "inherit")
										}
									>
										Plano
									</NavLink>
								</NavItem>
								<NavItem>
									<NavLink
										className={
											activeTab === "2" ? "active" : ""
										}
										onClick={() => setActiveTab("2")}
										style={{
											cursor: "pointer",
											color:
												activeTab === "2"
													? "#2a3042"
													: "inherit",
										}}
										onMouseEnter={(e) =>
											(e.target.style.color = "#2a3042")
										}
										onMouseLeave={(e) =>
										(e.target.style.color =
											activeTab === "2"
												? "#2a3042"
												: "inherit")
										}
									>
										Configuração
									</NavLink>
								</NavItem>
							</Nav>

							{/* Conteúdo das abas */}
							<TabContent
								activeTab={activeTab}
								style={{ marginTop: 20 }}
							>
								{/* Aba Plano */}
								<TabPane tabId="1">
									<Row>
										<Col md="8">
											<FormGroup className="mb-3">
												<Label htmlFor="guideline">
													Diretriz{" "}
													<span style={{ color: "red" }}>
														*
													</span>
												</Label>
												<Input
													name="guideline"
													placeholder="Diretriz do plano de ação"
													type="text"
													className="form-control"
													id="guideline"
													value={formData.guideline}
													onChange={handleInputChange}
													invalid={!!formErrors.guideline}
												/>
												{formErrors.guideline && (
													<FormFeedback>
														{formErrors.guideline}
													</FormFeedback>
												)}
											</FormGroup>
										</Col>
										<Col md="4">
											<FormGroup className="mb-3">
												<Label htmlFor="deadline">
													Prazo Final{" "}
													<span style={{ color: "red" }}>
														*
													</span>
												</Label>
												<Input
													name="deadline"
													placeholder="00/00/0000"
													type="date"
													className="form-control"
													id="deadline"
													value={formData.deadline}
													onChange={handleInputChange}
													invalid={!!formErrors.deadline}
												/>
												{formErrors.deadline && (
													<FormFeedback>
														{formErrors.deadline}
													</FormFeedback>
												)}
											</FormGroup>
										</Col>
									</Row>
								</TabPane>

								{/* Aba Configuração */}
								<TabPane tabId="2">
									<Row>
										<Col md="12">
											<FormGroup className="mb-3">
												<Label htmlFor="collaborators">
													Quem pode ver
												</Label>
												<Select
													menuPortalTarget={document.body}
													menuPosition="fixed"
													isMulti
													styles={customStyles}
													placeholder="Selecione quem pode ver o plano de ação"
													onChange={
														handleMultiSelectChange
													}
													options={usuarios}
													value={selectedOptions}
												/>
											</FormGroup>
										</Col>
									</Row>
									<Row>
										<Col md="6">
											<FormGroup className="mb-3">
												<Label htmlFor="year_of_use">
													Ano Utilizado
												</Label>
												<Select
													menuPortalTarget={document.body}
													menuPosition="fixed"
													styles={customStyles}
													placeholder="Selecione o Ano"
													onChange={(selectedOption) =>
														handleSelectChange(
															selectedOption,
															"year_of_use",
														)
													}
													value={years.find(
														(option) =>
															option.value ===
															formData.year_of_use,
													)}
													options={years}
												/>
											</FormGroup>
										</Col>
										<Col md="6">
											<FormGroup className="mb-3">
												<Label htmlFor="organizational_structure_id">
													Departamento{" "}
													<span style={{ color: "red" }}>
														*
													</span>
												</Label>
												<Select
													menuPortalTarget={document.body}
													menuPosition="fixed"
													styles={customStyles}
													placeholder="Selecione o departamento"
													onChange={(selectedOption) =>
														handleSelectChange(
															selectedOption,
															"organizational_structure_id",
														)
													}
													value={organizacoes.find(
														(option) =>
															option.value ===
															formData.organizational_structure_id,
													)}
													options={organizacoes}
													isInvalid={
														!!formErrors.organizational_structure_id
													}
												/>
												{formErrors.organizational_structure_id && (
													<div
														style={{
															color: "red",
															marginTop: 5,
														}}
													>
														{
															formErrors.organizational_structure_id
														}
													</div>
												)}
											</FormGroup>
										</Col>
									</Row>
									<Row>
										<Col md="6">
											<FormGroup className="mb-3">
												<Label htmlFor="approving_id">
													Aprovador{" "}
													<span style={{ color: "red" }}>
														*
													</span>
												</Label>
												<Select
													menuPortalTarget={document.body}
													menuPosition="fixed"
													styles={customStyles}
													placeholder="Selecione o aprovador"
													onChange={(selectedOption) =>
														handleSelectChange(
															selectedOption,
															"approving_id",
														)
													}
													value={usuarios.find(
														(option) =>
															option.value ===
															formData.approving_id,
													)}
													options={usuarios}
													isInvalid={
														!!formErrors.approving_id
													}
												/>
												{formErrors.approving_id && (
													<div
														style={{
															color: "red",
															marginTop: 5,
														}}
													>
														{formErrors.approving_id}
													</div>
												)}
											</FormGroup>
										</Col>
										<Col md="6">
											<FormGroup className="mb-3">
												<Label htmlFor="responsible_id">
													Responsável{" "}
													<span style={{ color: "red" }}>
														*
													</span>
												</Label>
												<Select
													menuPortalTarget={document.body}
													menuPosition="fixed"
													styles={customStyles}
													placeholder="Selecione o responsável"
													onChange={(selectedOption) =>
														handleSelectChange(
															selectedOption,
															"responsible_id",
														)
													}
													value={usuarios.find(
														(option) =>
															option.value ===
															formData.responsible_id,
													)}
													options={usuarios}
													isInvalid={
														!!formErrors.responsible_id
													}
												/>
												{formErrors.responsible_id && (
													<div
														style={{
															color: "red",
															marginTop: 5,
														}}
													>
														{formErrors.responsible_id}
													</div>
												)}
											</FormGroup>
										</Col>
									</Row>
								</TabPane>
							</TabContent>
						</Form>

						{data?.id && activeTab === "1" && (
							<div style={{ marginTop: 25 }}>
								<Divider style={{ marginBottom: 15 }} />
								<Row className="mb-4">
									<Col
										md="12"
										className="d-flex justify-content-between align-items-center"
									>
										<h4 className="mb-0">Objetivos</h4>
										<Button
											style={{
												background: "none",
												padding: 10,
												cursor: "pointer",
												color: "black",
												width: 150,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												gap: 2,
												marginBottom: 10,
											}}
											onClick={setObjectiveModal}
										>
											<Plus size={16} /> Novo Objetivo
										</Button>
									</Col>
									<Col md="12">
										<ObjetivosAccordion
											objetivos={objetivos}
											getObjetivos={fetchObjetivos}
											planCollaborators={formData.collaborators}
										/>
									</Col>
								</Row>
								<ModalCreateEditObjective
									show={openModalObj}
									onCloseClick={onCloseModalObj}
									data={{}}
									getObjetivos={fetchObjetivos}
									action_plans_id={data?.id}
									action_plan_deadline={formData.deadline}
									allowedResponsibleUserIds={formData.collaborators}
								/>
							</div>
						)}
					</div>
				</div>
			</Loading>

			<CopyActionPlanModal
				isOpen={isCopyModalOpen}
				toggle={() => setIsCopyModalOpen(false)}
				onCopy={handleConfirmCopy}
				actionPlanData={data}
			/>
		</React.Fragment>
	);
};

PlanosDeAcaoForm.propTypes = {
	preGlobalFilteredRows: PropTypes.any,
};

export default withRouter(PlanosDeAcaoForm);
